import { AbstractComponent } from "appworks/components/abstract-component";
import { Layers } from "appworks/graphics/layers/layers";
import { PIXIElement } from "appworks/graphics/pixi/group";
import { Contract } from "appworks/utils/contracts/contract";
import { Timer } from "appworks/utils/timer";
import { Easing, Tween } from "appworks/utils/tween";
import { filters } from "pixi.js";

export class EpicUIColourComponent extends AbstractComponent {
    private filter: filters.ColorMatrixFilter = new filters.ColorMatrixFilter();
    private elements: PIXIElement[] = [];

    private initialHue: number = 0;
    private initialSaturation: number = 0;
    private initialLight: number = 1;

    private previousHue: number = 0;
    private previousSaturation: number = 0;
    private previousLight: number = 1;

    private hue: number = 0;
    private saturation: number = 0;
    private light: number = 1;

    private rainbowMode: boolean = false;
    private rainbowInterval?: number;

    public init() {
/////////////////////////////////////////////////////////
//////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////
//////////
//////////////////

        this.collectSprites("SmartButton", [], [
            "spin"
        ]);

        this.collectSprites("Turbo", [], [
            "turbo_toggle_on",
            "turbo_toggle_off"
        ]);

        this.collectSprites("Autoplay", [], [
            "ui_autoplay_on",
            "ui_autoplay_off",
            "autoplay_stop_button"
        ], [
            "autoplay_count"
        ]);

        this.collectSprites("BetBar", [
            "bg",
            "portrait_bg"
        ], [
            "stake_up",
            "stake_down",
            "sound_toggle_on",
            "sound_music_off",
            "sound_toggle_off",
            "sound_toggle_loading",
            "turbo_toggle_on",
            "turbo_toggle_off",
            "ui_paytable_on",
            "ui_paytable_off",
            "ui_settings_on",
            "ui_settings_off",
            "portrait_stake_up",
            "portrait_stake_down"
        ]);

        this.collectSprites("Header", [
            "time_bg",
            "name_bg",
            "gamename_bg"
        ]);

        this.collectSprites("MenuContent", [
            "bg_landscape",
            "bg_portrait",
            "autoplay_background"
        ], [
            "history",
            "external_rules",
            "home",
            "close",
            "stop_on_feature_on",
            "stop_on_feature_off",
            "autoplay_start_button",
            "autoplay_radio_1",
            "autoplay_radio_2",
            "autoplay_radio_3",
            "autoplay_radio_4",
            "autoplay_radio_5",
            "loss_limit_radio_1",
            "loss_limit_radio_2",
            "loss_limit_radio_3",
            "loss_limit_radio_4",
            "loss_limit_radio_5",
            "single_win_limit_radio_1",
            "single_win_limit_radio_2",
            "single_win_limit_radio_3",
            "single_win_limit_radio_4",
            "single_win_limit_radio_5"
        ]);
    }

    /**
     * Set the UI color for the base game
     */
    public setInitialAdjustment(h: number, s: number, l: number): void {
        this.initialHue = h;
        this.initialSaturation = s;
        this.initialLight = l;

        this.previousHue = h;
        this.previousSaturation = s;
        this.previousLight = l;

        this.update(h, s, l);
    }

    /**
     * Reset the UI color to the base game color
     */
    public resetAdjustment(): void {
        this.update(this.initialHue, this.initialSaturation, this.initialLight);
    }

    /**
     * Reset the UI color to the base game color
     */
    public resetAdjustmentFade(duration: number): Contract<void> {
        return this.adjustFade(this.initialHue, this.initialSaturation, this.initialLight, duration);
    }

    /**
     * Change UI color for non-base game features (i.e freespins)
     */
    public adjust(h: number, s: number, l: number): void {
        this.previousHue = h;
        this.previousSaturation = s;
        this.previousLight = l;

        this.update(h, s, l);
    }

    /**
     * Change UI color for non-base game features (i.e freespins)
     */
    public adjustFade(h: number, s: number, l: number, duration: number): Contract<void> {
        this.previousHue = h;
        this.previousSaturation = s;
        this.previousLight = l;

        const target = { h: this.hue, s: this.saturation, l: this.light };
        return Contract.wrapTween(new Tween(target)
            .to({ h, s, l }, duration)
            .easing(Easing.Linear.None)
            .onUpdate(() => {
                this.update(target.h, target.s, target.l);
            })
            .onComplete(() => {
                this.update(h, s, l);
            })
        );
    }

    /**
     * When turned off, will return to whatever was last set via "adjust" / "adjustFade"
     */
    public rainbow(on: boolean, speed: number = 10): void {
        this.rainbowMode = on;

        if (this.rainbowMode) {
            if (this.rainbowInterval !== undefined) {
                Timer.clearInterval(this.rainbowInterval);
                this.rainbowInterval = undefined;
            }
            this.rainbowInterval = Timer.setInterval(() => {
                const hue = (this.hue + speed) % 360;
                this.update(hue, this.saturation, this.light);
            }, 0);
        } else {
            if (this.rainbowInterval !== undefined) {
                Timer.clearInterval(this.rainbowInterval);
                this.rainbowInterval = undefined;
                this.resetAdjustmentToPrevious();
            }
        }
    }

    /**
     * When complete, will return to whatever was last set via "adjust" / "adjustFade"
     */
    public rainbowOnce(duration: number = 2000): Contract<void> {
        if (this.rainbowMode) {
            return Contract.empty();
        } else {
            const startHue: number = this.hue;
            const adjustment = { h: 0 };
            this.rainbowMode = true;
            return Contract.wrapTween(
                new Tween(adjustment)
                    .to({ h: 360 }, duration)
                    .easing(Easing.Linear.None)
                    .onUpdate(() => {
                        const hue = (startHue + adjustment.h) % 360;
                        this.update(hue, this.saturation, this.light);
                    })
                    .onComplete(() => {
                        this.rainbowMode = false;
                        this.resetAdjustmentToPrevious();
                    })
            );
        }
    }

    private update(h: number, s: number, l: number) {
        this.hue = h;
        this.saturation = s;
        this.light = l;

        this.filter.reset();
        this.filter.brightness(l, true);
        this.filter.saturate(s, true);
        this.filter.hue(h, true);
    }

    private resetAdjustmentToPrevious() {
        this.update(this.previousHue, this.previousSaturation, this.previousLight);
    }

    private collectSprites(layerName: string, sprites: string[], buttons: string[] = [], texts: string[] = []): void {
        const layer = Layers.get(layerName);
        if (layer) {
            layer.onSceneEnter.add(() => {
                this.elements.push(...sprites.map((x) => layer.getSprite(x)));
                this.elements.push(...buttons.map((x) => layer.getButton(x)));
                this.elements.push(...texts.map((x) => layer.getText(x)));
                this.elements.push(...texts.map((x) => layer.getBitmapText(x)));
                this.elements = this.elements.filter((x) => x !== undefined && x !== null);
                this.elements.forEach((x) => x.filters = [this.filter]);
            });

            layer.defaultScene().execute();
        }
    }
}
